/**
 * What is happening on every board, above the tabs and visible from all of
 * them — an admin walking the room needs the boards whatever screen they left
 * open.
 *
 * Fed by the TournamentRoom socket rather than /boards, because two of the
 * things drawn here exist nowhere else: whose throw it is, and scores that
 * move on their own. See src/lib/tournamentRoom.js.
 */

import { useState } from 'react';
import { throwingEntryId, matchAverages } from '../../lib/tournamentRoom.js';
import { formatAvg } from '../../lib/format.js';

const STAGE_LABELS = { group: 'Group', knockout: 'Knockout' };

/**
 * Knockout rounds count DOWN to the final — round 1 IS the final — so the
 * round number is the exponent of the bracket size at that point.
 */
function stageLabel(match) {
  if (!match) return null;
  if (match.stage !== 'knockout') return STAGE_LABELS[match.stage] ?? match.stage;

  switch (match.round) {
    case 1:  return 'Final';
    case 2:  return 'Semi-Final';
    case 3:  return 'Quarter-Final';
    default: return `Last ${2 ** (match.round ?? 1)}`;
  }
}

/* ------------------------------------------------------------------------- */

function PlayerRow({ name, legs, remaining, throwing, average }) {
  return (
    <div className={`hd-board-player ${throwing ? 'hd-board-throwing' : ''}`}>
      <span className="hd-board-player-name">
        {name ?? <span className="hd-muted">TBC</span>}
        {/* The tint alone says "throwing" only to someone who can see it and
            knows the convention. */}
        {throwing && <span className="hd-visually-hidden"> — throwing</span>}
      </span>

      {average != null && (
        <span className="hd-board-avg" title="Three-dart average">
          {formatAvg(average)}
        </span>
      )}

      <span className="hd-board-legs" title="Legs won">{legs ?? 0}</span>

      {remaining != null && (
        <span className="hd-board-remaining">{remaining}</span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Whether a scoring device is on this board, and whether it is behind.
 *
 * PAIRED AND CONNECTED ARE DIFFERENT QUESTIONS. A pairing survives a flat
 * battery on purpose, so a board can be paired with nothing on the end of it.
 * Unpaired means no tablet has ever claimed it tonight; Offline means one has
 * and it is not answering.
 *
 * THE COUNT IS SHOWN WHENEVER IT IS NON-ZERO, online or off — not only when
 * offline, as first sketched. An offline tablet cannot tell us its queue is
 * growing, so the figure goes stale in exactly the situation it sounds most
 * useful in, and "(0 unsynced)" about a device we cannot reach would be
 * asserting something we do not know. What it IS honest about is a scorer that
 * is connected and still not draining — which is the case where somebody needs
 * to go and look at it, and the case the offline-only version would have hidden.
 */
function BoardStatus({ board }) {
  const unsynced = board.unsynced ?? 0;

  const state = !board.paired ? 'unpaired'
    : board.connected ? 'online'
    : 'offline';

  const LABEL = { unpaired: 'Unpaired', online: 'Online', offline: 'Offline' };

  return (
    <span className={`hd-board-status hd-board-status-${state}`}>
      <span className="hd-board-dot" aria-hidden="true" />
      {LABEL[state]}
      {unsynced > 0 && (
        <span className="hd-board-unsynced">
          ({unsynced} unsynced)
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------------- */

function BoardCard({ board, completedMatch, onForfeit }) {
  // A finished match is held by the room hook until the next one starts, so
  // the board does not blank the moment a match ends.
  const match = board.match ?? completedMatch ?? null;
  const isLive = match?.status === 'live';
  const upcoming = board.upcoming?.[0] ?? null;

  // AVERAGES ONLY ONCE THE MATCH IS DONE. Mid-match an average is a number
  // that swings wildly on one visit and invites a decision it cannot support.
  // finalAverages is the room's own figure for a finished match. Deriving from
  // the held snapshot misses the deciding leg, because the visit that wins it
  // also ends the match. See tournamentRoom.js.
  const averages = match && !isLive
    ? match.finalAverages ?? matchAverages(match)
    : {};
  const throwing = throwingEntryId(match);

  const legsWon = match?.legsWon ?? {};

  // A snapshot carries currentLegId and no leg NUMBER, so the number comes off
  // the leg — one lookup serving both it and the remaining scores.
  const currentLeg = isLive
    ? (match.legs ?? []).find((l) => l.id === match.currentLegId) ?? null
    : null;

  const remaining = currentLeg?.remaining ?? {};

  return (
    <div className="hd-board-card">
      <div className="hd-board-head">
        <span className="hd-board-number">Board {board.boardNumber}</span>
        {match && <span className="hd-board-stage">· {stageLabel(match)}</span>}

        <BoardStatus board={board} />

        {match && isLive && (
          <button
            type="button"
            className="hd-btn hd-btn-quiet hd-btn-sm hd-board-forfeit"
            onClick={() => onForfeit(match)}
          >
            Forfeit
          </button>
        )}
      </div>

      {match ? (
        <>
          <div className="hd-board-players">
            {[match.player1_id, match.player2_id].map((entryId, i) => (
              <PlayerRow
                key={entryId ?? `slot-${i}`}
                name={i === 0 ? match.player1_name : match.player2_name}
                legs={legsWon[entryId]}
                remaining={remaining[entryId]}
                throwing={throwing != null && throwing === entryId}
                average={averages[entryId]}
              />
            ))}
          </div>

          <div className="hd-board-foot">
            {isLive
              ? `Leg ${currentLeg?.leg_number ?? '—'} · first to ${match.legsToWin}`
              : 'Match complete'}
          </div>
        </>
      ) : (
        <div className="hd-board-idle">
          {upcoming
            ? `Next: ${upcoming.player1_name ?? 'TBC'} v ${upcoming.player2_name ?? 'TBC'}`
            : 'Nothing queued'}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

export default function BoardsPanel({ status, snapshot, completedByBoard, onForfeit }) {
  const [open, setOpen] = useState(true);
  const boards = snapshot?.boards ?? [];

  return (
    <section className="hd-boards">
      <div className="hd-row-between">
        <button
          type="button"
          className="hd-disclosure-toggle"
          aria-expanded={open}
          aria-controls="boards-grid"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="hd-disclosure-caret" aria-hidden="true" />
          Boards{boards.length > 0 && ` (${boards.length})`}
        </button>

        <span className={`hd-badge hd-badge-${status === 'connected' ? 'success' : 'neutral'}`}>
          {status === 'connected' ? 'Live' : 'Not connected'}
        </span>
      </div>

      <div id="boards-grid" hidden={!open}>
        {status !== 'connected' && boards.length === 0 ? (
          // Named plainly: in development this is almost always the rooms
          // Worker not running, and "no boards" would send someone hunting
          // through the draw for a problem that is not there.
          <div className="hd-empty">
            Not connected to the tournament room. Live scores need the rooms
            Worker running (<code>npm run dev:rooms</code>).
          </div>
        ) : boards.length === 0 ? (
          <div className="hd-empty">No boards yet — nothing has been drawn.</div>
        ) : (
          <div className="hd-board-grid">
            {boards.map((board) => (
              <BoardCard
                key={board.boardNumber}
                board={board}
                completedMatch={completedByBoard[board.boardNumber] ?? null}
                onForfeit={onForfeit}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
